import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ReactFlow, { Background, BackgroundVariant, Controls, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import Modal from 'src/components/layouts/Modal/Modal';
import Badge from 'src/components/ui/Badge/Badge';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { workflowEdgeTypes, workflowNodeTypes } from 'src/components/nodes/nodeTypes';
import useWorkflows from 'src/hooks/useWorkflows';
import { getWorkflows } from 'src/redux/workflows/selectors';
import { WorkflowBlueprint } from 'src/services/workflows';
import { getColorForTriggerCategory } from 'src/utils/general';

type CanvasProps = { blueprint: WorkflowBlueprint };

const BlueprintCanvas: React.FC<CanvasProps> = ({ blueprint }) => {
  const workflowsState = useSelector(getWorkflows);
  const { nodes, edges, loadFromTree } = useWorkflows(workflowsState);

  useEffect(() => {
    loadFromTree(blueprint.root, blueprint.trigger.name);
  }, [blueprint, loadFromTree]);

  const readOnlyNodes = useMemo(
    () =>
      nodes.filter(node => node.type !== 'plus').map(node => ({ ...node, data: { ...node.data, isEditMode: false } })),
    [nodes],
  );

  const readOnlyEdges = useMemo(() => {
    const plusIds = new Set(nodes.filter(node => node.type === 'plus').map(node => node.id));
    const bridged = edges
      .filter(edge => plusIds.has(edge.target))
      .flatMap(incoming =>
        edges
          .filter(outgoing => outgoing.source === incoming.target)
          .map(outgoing => ({ ...incoming, id: `${incoming.source}-${outgoing.target}`, target: outgoing.target })),
      );
    return [...edges.filter(edge => !plusIds.has(edge.source) && !plusIds.has(edge.target)), ...bridged];
  }, [nodes, edges]);

  return (
    <div style={{ width: '100%', height: '52vh' }}>
      <ReactFlow
        nodeTypes={workflowNodeTypes}
        edgeTypes={workflowEdgeTypes}
        nodes={readOnlyNodes}
        edges={readOnlyEdges}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        panOnDrag
        proOptions={{ hideAttribution: true }}>
        <Controls showInteractive={false} />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

type Props = {
  blueprint: WorkflowBlueprint | null;
  onClose: () => void;
  onUse: (blueprint: WorkflowBlueprint) => void;
};

const BlueprintPreviewModal: React.FC<Props> = ({ blueprint, onClose, onUse }) => {
  const { t } = useTranslation();

  return (
    <Modal isOpen={!!blueprint} onClose={onClose} hasCloseButton hasDefaultPadding wide>
      {blueprint && (
        <div className="flex flex-col gap-y-16">
          <div className="flex flex-col gap-y-8">
            <div className="flex items-center gap-x-12">
              <h2 className="typo-title-24">{blueprint.name}</h2>
              <Badge
                expanded
                label={blueprint.trigger.name}
                colorOverride={getColorForTriggerCategory(blueprint.category)}
              />
            </div>
            {blueprint.description && <p className="typo-text-14 text-faded">{blueprint.description}</p>}
          </div>
          <ReactFlowProvider>
            <BlueprintCanvas blueprint={blueprint} />
          </ReactFlowProvider>
          <div className="flex justify-end gap-x-8">
            <Button tone={ButtonToneProps.SECONDARY} title={t('general.close')} onClick={onClose} />
            <Button
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('workflows.create_overlay.use_blueprint')}
              onClick={() => onUse(blueprint)}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BlueprintPreviewModal;
